import { styled } from '@mui/material/styles';
import { Input } from '@components/Input';
import { Checkbox } from '@components/Checkbox';
import { Typography } from '@components/Typography';
import DragAndDrop, { SortableItem } from '@components/DragAndDrop';
import FieldWithActions, { rowActions } from '@components/FieldWithActions';
import { useTranslation } from '@hooks';
import { MoveIcon } from 'assets/images/icons';
import { CheckboxRow } from './styles';

const OptionWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: 24,
});

const OptionRow = ({ opt, index, count, listeners, attributes, isView, onLabelChange, onAdd, onDelete, t }) => (
    <OptionWrapper>
        <span
            {...listeners}
            {...attributes}
            style={{ cursor: 'grab', display: 'flex', alignItems: 'center', flexShrink: 0 }}
        >
            <MoveIcon />
        </span>
        <FieldWithActions
            actions={rowActions({
                index,
                count,
                onAdd,
                onDelete: () => onDelete(opt.id),
                disabled: isView,
                t,
            })}
        >
            <Input
                inputProps={{
                    value: opt.label,
                    onChange: e => onLabelChange(opt.id, e.target.value),
                }}
                label={`${t('FeedbackSurveys.optionLabel')} ${index + 1}`}
                fullWidth
                required
                error={!opt.label?.trim()}
                helperText={!opt.label?.trim() ? t('validation.required') : ''}
                disabled={isView}
            />
        </FieldWithActions>
    </OptionWrapper>
);

const QuestionOptions = ({ options = [], onChange, isView }) => {
    const { t } = useTranslation();

    const visibleOptions = options.filter(opt => !opt.hidden && !opt.isOther);
    const hasOtherOption = options.some(opt => opt.isOther && !opt.hidden);

    const handleLabelChange = (id, value) => {
        const updated = options.map(opt => (opt.id === id ? { ...opt, label: value } : opt));
        onChange(updated);
    };

    const handleAdd = () => {
        const maxId = options.length > 0 ? Math.max(...options.map(opt => opt.id)) : -1;
        const newOption = {
            id: maxId + 1,
            label: '',
            position: visibleOptions.length,
            hidden: false,
        };
        onChange([...options, newOption]);
    };

    const handleDelete = id => {
        const updated = options.map(opt => (opt.id === id ? { ...opt, hidden: true } : opt));
        onChange(updated);
    };

    const handleReorder = reordered => {
        const reorderedWithPositions = reordered.map((opt, idx) => ({ ...opt, position: idx }));
        const hiddenOptions = options.filter(opt => opt.hidden);
        const otherOption = options.find(opt => opt.isOther && !opt.hidden);
        const result = [...reorderedWithPositions, ...hiddenOptions];
        if (otherOption) {
            result.push({ ...otherOption, position: reorderedWithPositions.length });
        }
        onChange(result);
    };

    const handleToggleOther = (_, checked) => {
        if (checked) {
            const existingOther = options.find(opt => opt.isOther);
            if (existingOther) {
                const updated = options.map(opt =>
                    opt.isOther ? { ...opt, hidden: false, position: visibleOptions.length } : opt,
                );
                onChange(updated);
            } else {
                const maxId = options.length > 0 ? Math.max(...options.map(opt => opt.id)) : -1;
                const otherOption = {
                    id: maxId + 1,
                    label: t('FeedbackSurveys.other'),
                    position: visibleOptions.length,
                    hidden: false,
                    isOther: true,
                };
                onChange([...options, otherOption]);
            }
        } else {
            const updated = options.map(opt => (opt.isOther ? { ...opt, hidden: true } : opt));
            onChange(updated);
        }
    };

    const itemIds = visibleOptions.map(opt => String(opt.id));

    return (
        <div style={{ marginBottom: 8 }}>
            <DragAndDrop items={itemIds} data={visibleOptions} onDragEndCb={handleReorder}>
                {visibleOptions.map((opt, index) => (
                    <SortableItem key={opt.id} id={String(opt.id)}>
                        <OptionRow
                            opt={opt}
                            index={index}
                            count={visibleOptions.length}
                            isView={isView}
                            onLabelChange={handleLabelChange}
                            onAdd={handleAdd}
                            onDelete={handleDelete}
                            t={t}
                        />
                    </SortableItem>
                ))}
            </DragAndDrop>
            <CheckboxRow>
                <Checkbox
                    disabled={isView}
                    inputProps={{ 'aria-label': 'controlled' }}
                    onChange={e => handleToggleOther(e, e.target.checked)}
                    checked={hasOtherOption}
                />
                <Typography variant="body2">{t('FeedbackSurveys.includeOtherOption')}</Typography>
            </CheckboxRow>
        </div>
    );
};

export default QuestionOptions;
